'use no memo';

import type { GradePublic } from '@repo/types/grades';
import type { UserPublic } from '@repo/types/users';
import type { ColumnDef } from '@tanstack/react-table';

import { useGetGroupGrades } from '@repo/api/grade/useGetGroupGrades';
import { cn } from '@repo/lib/cn';
import { flexRender } from '@tanstack/react-table';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, UserIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import type { DateRangeFilterValue } from '@/components/features/filters/DateRangePicker';

import DateRangePicker from '@/components/features/filters/DateRangePicker';
import GradeBadge from '@/components/features/grades/GradeBadge';
import GradeDetailsPopup from '@/components/features/grades/GradeDetailsPopup';
import { TanstackTableHeaderContent } from '@/components/features/manage/tanstack-table/TanstackTable';
import { useTanstackTable } from '@/components/features/manage/tanstack-table/useTanstackTable';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SearchInput } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { useGradeOriginLink } from '@/hooks/grade';
import { getAvatar } from '@/utils/getAvatar';

import { GRADE_ORIGIN_CONFIG } from './gradeOrigin';

type SortBy = 'date' | 'name';
type SortDir = 'asc' | 'desc';

interface GradeItem {
  key: string;
  name: string;
  date: string;
  tsMin: number;
  tsMax: number;
  search: string;
  grade: GradePublic;
}

interface GroupGradebookProps {
  groupId: number;
}

/**
 * A column stands for the item its grades came from, so it is keyed, named and
 * dated by that item. Grades issued by hand — and older ones, recorded before
 * the origin was stored on the grade — fall back to their own name, so each
 * still gets a column of its own instead of piling into a shared one.
 */
function originItem(grade: GradePublic): Pick<GradeItem, 'key' | 'name' | 'date'> {
  const unlinked = {
    key: `${grade.originType}:${grade.name}`,
    name: grade.name,
    date: grade.createdAt,
  };

  switch (grade.originType) {
    case 'quizResponse':
      return grade.quizId === null
        ? unlinked
        : {
            key: `quiz:${grade.quizId}`,
            name: grade.quiz?.name ?? grade.name,
            date: grade.quiz?.deadline ?? grade.quiz?.available ?? grade.createdAt,
          };
    case 'assignmentResponse':
      return grade.assignmentId === null
        ? unlinked
        : {
            key: `assignment:${grade.assignmentId}`,
            name: grade.assignment?.name ?? grade.name,
            date: grade.assignment?.dueTo ?? grade.assignment?.available ?? grade.createdAt,
          };
    case 'other':
      return unlinked;
  }
}

function GradeItemHeader({ item }: { item: GradeItem }) {
  const origin = useGradeOriginLink(item.grade);
  const config = GRADE_ORIGIN_CONFIG[item.grade.originType];
  const className = 'flex w-full flex-col items-center gap-1 px-3 py-2 text-center';
  const content = (
    <>
      <span className={'text-foreground text-xs font-medium group-hover:underline'}>{item.name}</span>
      <span className={'text-muted-foreground text-xs'}>{format(parseISO(item.date), 'd MMM yyyy')}</span>
      <Badge variant={config.variant} size={'sm'} border>
        {config.label}
      </Badge>
    </>
  );

  if (!origin) {
    return <div className={className}>{content}</div>;
  }

  return (
    <Link
      to={origin.to}
      aria-label={`Przejdź do: ${origin.name}`}
      className={cn(className, 'outline-focus-ring-primary hover:bg-accent/50 group outline-0 focus-visible:outline-3 focus-visible:-outline-offset-3')}
    >
      {content}
    </Link>
  );
}

function GradebookSkeleton() {
  return (
    <div className={'border-border-primary overflow-hidden rounded-md border'}>
      <div className={'w-full overflow-x-auto'}>
        <Table>
          <TableHeader>
            <TableRow className={'bg-input border-border-primary'}>
              <TableCell className={'min-w-64 px-3 py-2'}>
                <Skeleton className={'h-4 w-24'} />
              </TableCell>
              {Array.from({ length: 4 }).map((_, i) => (
                <TableCell key={i} className={'border-border-primary min-w-32 border-l px-3 py-2'}>
                  <div className={'flex flex-col items-center gap-1'}>
                    <Skeleton className={'h-3 w-16'} />
                    <Skeleton className={'h-3 w-12'} />
                    <Skeleton className={'h-4 w-12 rounded-md'} />
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 6 }).map((_, rowIdx) => (
              <TableRow key={rowIdx} className={'border-border-primary'}>
                <TableCell className={'min-w-64 px-3 py-2'}>
                  <div className={'flex items-center gap-2'}>
                    <Skeleton className={'size-8 rounded-full'} />
                    <Skeleton className={'h-4 w-32'} />
                  </div>
                </TableCell>
                {Array.from({ length: 4 }).map((_, colIdx) => (
                  <TableCell key={colIdx} className={'border-border-primary min-w-32 border-l px-3 py-2'}>
                    <Skeleton className={'mx-auto h-6 w-12'} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function GroupGradebook({ groupId }: GroupGradebookProps) {
  const { data, isPending } = useGetGroupGrades(groupId);

  const [dateRange, setDateRange] = useState<DateRangeFilterValue | undefined>(undefined);
  const [sortBy, setSortBy] = useState<SortBy>('date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [columnSearch, setColumnSearch] = useState<string>('');

  const { items, gradesByStudent } = useMemo(() => {
    const itemMap = new Map<string, GradeItem>();
    const byStudent = new Map<number, Map<string, GradePublic[]>>();

    for (const grade of data?.grades ?? []) {
      const { key, name, date } = originItem(grade);

      const ts = parseISO(date).getTime();
      const existing = itemMap.get(key);
      if (!existing) {
        itemMap.set(key, {
          key,
          name,
          date,
          tsMin: ts,
          tsMax: ts,
          search: `${name} ${GRADE_ORIGIN_CONFIG[grade.originType].label}`.toLowerCase(),
          grade,
        });
      } else {
        if (ts < existing.tsMin) existing.tsMin = ts;
        if (ts > existing.tsMax) {
          existing.tsMax = ts;
          existing.date = date;
        }
      }

      const studentMap = byStudent.get(grade.userId) ?? new Map<string, GradePublic[]>();
      const list = studentMap.get(key) ?? [];
      list.push(grade);
      studentMap.set(key, list);
      byStudent.set(grade.userId, studentMap);
    }

    return { items: [...itemMap.values()], gradesByStudent: byStudent };
  }, [data?.grades]);

  const visibleItems = useMemo<GradeItem[]>(() => {
    const start = dateRange?.start ? startOfDay(new Date(dateRange.start)).getTime() : undefined;
    const end = dateRange?.end ? endOfDay(new Date(dateRange.end)).getTime() : undefined;
    const query = columnSearch.trim().toLowerCase();

    return items
      .filter((item) => {
        if (start !== undefined && item.tsMax < start) return false;
        if (end !== undefined && item.tsMin > end) return false;
        if (query && !item.search.includes(query)) return false;
        return true;
      })
      .sort((a, b) => {
        const comparison = sortBy === 'name' ? a.name.localeCompare(b.name, 'pl') : a.tsMax - b.tsMax;
        return sortDir === 'asc' ? comparison : -comparison;
      });
  }, [items, dateRange, columnSearch, sortBy, sortDir]);

  const rows = useMemo<UserPublic[]>(() => [...(data?.users ?? [])].sort((a, b) => `${a.surname} ${a.name}`.localeCompare(`${b.surname} ${b.name}`, 'pl')), [data?.users]);

  const columns = useMemo<ColumnDef<UserPublic>[]>(() => {
    const studentColumn: ColumnDef<UserPublic> = {
      id: 'student',
      header: 'Uczeń',
      accessorFn: (row) => `${row.name} ${row.surname}`,
      enableSorting: true,
      cell: ({ row }) => {
        const student = row.original;
        return (
          <div className={'flex items-center gap-2'}>
            <Avatar size={'sm'}>
              <AvatarImage src={getAvatar(student.avatarURL)} alt={`${student.name} ${student.surname}`} />
              <AvatarFallback>
                <UserIcon className={'size-3.5'} />
              </AvatarFallback>
            </Avatar>
            <span className={'text-foreground truncate text-sm font-medium'}>
              {student.name} {student.surname}
            </span>
          </div>
        );
      },
    };

    const itemColumns = visibleItems.map<ColumnDef<UserPublic>>((item) => ({
      id: item.key,
      header: () => <GradeItemHeader item={item} />,
      enableSorting: false,
      cell: ({ row }) => {
        const grades = gradesByStudent.get(row.original.id)?.get(item.key);
        if (!grades?.length) return null;

        return (
          <div className={'flex flex-wrap gap-1'}>
            {grades.map((grade) => (
              <GradeDetailsPopup key={grade.id} grade={grade}>
                <button type={'button'} className={'outline-focus-ring-primary rounded-md outline-0 focus-visible:outline-3'}>
                  <GradeBadge grade={grade} />
                </button>
              </GradeDetailsPopup>
            ))}
          </div>
        );
      },
    }));

    return [studentColumn, ...itemColumns];
  }, [visibleItems, gradesByStudent]);

  const { table } = useTanstackTable<UserPublic>({
    name: `group-gradebook-${groupId}`,
    columns,
    data: rows,
    getRowId: (row) => String(row.id),
  });

  const tableRows = table.getRowModel().rows;

  return (
    <div className={'base-direction-col'}>
      <div className={'bg-background border-border-primary flex h-full w-full flex-col gap-4 rounded-2xl border p-4'}>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/groups/${groupId}`}>Grupa</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Oceny</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {isPending ? (
          <GradebookSkeleton />
        ) : (
          <>
            <div className={'flex flex-wrap items-center justify-between gap-3'}>
              <div className={'flex flex-wrap items-center gap-2'}>
                <DateRangePicker value={dateRange} onChange={setDateRange} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant={'outline'} size={'sm'}>
                      {sortDir === 'asc' ? <ArrowUpNarrowWide /> : <ArrowDownWideNarrow />}
                      Sortuj: {sortBy === 'date' ? 'Data' : 'Nazwa'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align={'start'} className={'w-44'}>
                    <DropdownMenuLabel>Sortuj według</DropdownMenuLabel>
                    <DropdownMenuRadioGroup value={sortBy} onValueChange={(value) => setSortBy(value === 'name' ? 'name' : 'date')}>
                      <DropdownMenuRadioItem value={'date'}>Data</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value={'name'}>Nazwa</DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Kierunek</DropdownMenuLabel>
                    <DropdownMenuRadioGroup value={sortDir} onValueChange={(value) => setSortDir(value === 'desc' ? 'desc' : 'asc')}>
                      <DropdownMenuRadioItem value={'asc'}>Rosnąco</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value={'desc'}>Malejąco</DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <SearchInput className={'w-full max-w-xs'} placeholder={'Szukaj kolumn...'} setValue={setColumnSearch} />
            </div>

            {visibleItems.length === 0 && (
              <p className={'text-muted-foreground text-sm'}>{items.length === 0 ? 'W tej grupie nie wystawiono jeszcze żadnych ocen.' : 'Brak ocen pasujących do wybranych filtrów.'}</p>
            )}

            <div className={'border-border-primary overflow-hidden rounded-md border'}>
              <div className={'w-full overflow-x-auto'}>
                <Table>
                  <TableHeader>
                    <TanstackTableHeaderContent
                      table={table}
                      rowClassName={'bg-input border-border-primary'}
                      getHeadClassName={(headerId) => (headerId === 'student' ? 'min-w-64 align-middle' : 'border-border-primary min-w-32 border-l p-0 align-top')}
                    />
                  </TableHeader>
                  <TableBody>
                    {tableRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={columns.length} className={'text-muted-foreground py-6 text-center'}>
                          Brak uczniów do wyświetlenia.
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableRows.map((row) => (
                        <TableRow key={row.id} className={'border-border-primary'}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id} className={cn('px-3 py-2 align-middle', cell.column.id === 'student' ? 'min-w-64' : 'border-border-primary min-w-32 border-l')}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
